import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import logo from '../assets/navlogo.png';
import { FaHome, FaInfoCircle, FaBriefcase, FaEnvelope, FaFileAlt } from 'react-icons/fa';

// Add a resize lock for the entire page
let resizeTimer: NodeJS.Timeout | null = null;
const lockResize = () => {
  document.body.classList.add('resize-lock');
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    document.body.classList.remove('resize-lock');
  }, 400); // Longer delay to ensure no flicker
};

// Add this to your app once (could be moved to a higher component if needed)
if (typeof window !== 'undefined') {
  window.addEventListener('resize', lockResize);
  
  // Add a scroll class to handle iOS scroll behavior
  let scrollTimer: NodeJS.Timeout | null = null;
  window.addEventListener('scroll', () => {
    document.body.classList.add('is-scrolling');
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      document.body.classList.remove('is-scrolling');
    }, 100);
  });
}

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const burgerButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Combine closeMenu and scrollToTop for navigation links
  const handleNavLinkClick = () => {
    closeMenu(); // Close the mobile menu if open
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Always scroll to top
  };

  // Handler specifically for the logo (already scrolls to top)
  const handleLogoClick = (e: React.MouseEvent) => {
    // Prevent scroll if clicking the burger button itself (edge case)
    if (burgerButtonRef.current?.contains(e.target as Node)) return;
    closeMenu(); // Close menu if clicking logo while menu is open
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close menu on scroll
  const handleScroll = () => {
    if (isMenuOpen) setIsMenuOpen(false);
    burgerButtonRef.current?.blur(); // Remove focus from burger
  };

  // Close menu on click outside
  const handleClickOutside = (e: MouseEvent) => {
    // Ignore clicks on the burger button itself
    if (burgerButtonRef.current && burgerButtonRef.current.contains(e.target as Node)) {
      return;
    }
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setIsMenuOpen(false);
    }
  };

  // Debounce resize handler
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      document.body.classList.add('resize-lock'); // Add class to disable transitions
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsResizing(false);
        document.body.classList.remove('resize-lock'); // Remove class
        // Close menu on resize end if screen is now desktop size
        if (window.innerWidth > 768) {
          closeMenu();
        }
      }, 250); // Adjust debounce time as needed
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
      document.body.classList.remove('resize-lock'); // Clean up on unmount
    };
  }, []);

  // Add/remove scroll listener based on menu state
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]); // Re-run if isMenuOpen changes

  // Add/remove click outside listener based on menu state
  useEffect(() => {
    if (isMenuOpen) {
      // Use timeout to prevent immediate closing due to the click that opened it
      setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <nav className="navbar" ref={menuRef}>
      {/* Use handleLogoClick for the logo */}
      <Link to="/" className="navbar-logo" onClick={handleLogoClick}>
        <img src={logo} alt="IntelPro Logo" draggable={false} />
      </Link>

      <button
        ref={burgerButtonRef}
        className={`burger-menu ${isMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle navigation"
      >
        <span className="line"></span>
        <span className="line"></span>
        <span className="line"></span>
      </button>

      <div className={`navbar-links ${isMenuOpen ? 'active' : ''}`}>
        {/* Use handleNavLinkClick for all navigation links */}
        <NavLink to="/" onClick={handleNavLinkClick} end>
          <FaHome className="nav-icon" /><span>Home</span>
        </NavLink>
        <NavLink to="/about" onClick={handleNavLinkClick}>
          <FaInfoCircle className="nav-icon" /><span>About</span>
        </NavLink>
        <NavLink to="/services" onClick={handleNavLinkClick}>
          <FaBriefcase className="nav-icon" /><span>Services</span>
        </NavLink>
        {/* --- Add Case Studies Link --- */}
        <NavLink to="/case-studies" onClick={handleNavLinkClick}>
          <FaFileAlt className="nav-icon" /><span>Case Studies</span>
        </NavLink>
        {/* --- End Case Studies Link --- */}
        <NavLink to="/contact" onClick={handleNavLinkClick}>
          <FaEnvelope className="nav-icon" /><span>Contact</span>
        </NavLink>
      </div>
    </nav>
  );
};

export default Navbar;